import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { consoleIfNotProd } from 'chatspot-core/loggers/consoleIfNotProd';
import { CopilotChannelContext } from './CopilotChannelContext';
import { createCopilotChannel } from './createCopilotChannel';
import { noopCopilotTelemetry } from '../types/CopilotTelemetry';
import { jsx as _jsx } from "react/jsx-runtime";
// Run one seam method safely: fall back to the no-op when the consumer didn't
// implement it, and log (never propagate) a throwing implementation so
// telemetry can't break the caller.
function invokeTelemetry(key, impl,
// eslint-disable-next-line @typescript-eslint/no-explicit-any
...args) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (impl !== null && impl !== void 0 ? impl : noopCopilotTelemetry[key])(...args);
  } catch (error) {
    consoleIfNotProd.error(`copilot telemetry ${key} threw`, error);
    return undefined;
  }
}
const CopilotChannelProvider = ({
  children,
  createLayer,
  instanceId,
  telemetry: telemetryProp
}) => {
  // Keep the latest telemetry prop in a ref so an inline
  // `telemetry={createCopilotTelemetry(...)}` doesn't change the context value
  // identity (and every hook callback) on each parent render.
  const telemetryRef = useRef(telemetryProp);
  useEffect(() => {
    telemetryRef.current = telemetryProp;
  }, [telemetryProp]);
  // Stable identity (empty deps); each forwarder reads the latest prop from the
  // ref at call time, so an inline `telemetry={createCopilotTelemetry(...)}`
  // prop never re-identifies the context value or the hook callbacks.
  const telemetry = useMemo(() => ({
    skillInvoked: attributes => {
      var _telemetryRef$current;
      return invokeTelemetry('skillInvoked', (_telemetryRef$current = telemetryRef.current) === null || _telemetryRef$current === void 0 ? void 0 : _telemetryRef$current.skillInvoked, attributes);
    },
    skillInvokeDropped: reason => {
      var _telemetryRef$current2;
      return invokeTelemetry('skillInvokeDropped', (_telemetryRef$current2 = telemetryRef.current) === null || _telemetryRef$current2 === void 0 ? void 0 : _telemetryRef$current2.skillInvokeDropped, reason);
    },
    channelMessageSent: attributes => {
      var _telemetryRef$current3;
      return invokeTelemetry('channelMessageSent', (_telemetryRef$current3 = telemetryRef.current) === null || _telemetryRef$current3 === void 0 ? void 0 : _telemetryRef$current3.channelMessageSent, attributes);
    },
    appContextUpdated: attributes => {
      var _telemetryRef$current4;
      return invokeTelemetry('appContextUpdated', (_telemetryRef$current4 = telemetryRef.current) === null || _telemetryRef$current4 === void 0 ? void 0 : _telemetryRef$current4.appContextUpdated, attributes);
    }
  }), []);
  const channel = useMemo(() => createCopilotChannel({
    createLayer,
    instanceId
  }), [createLayer, instanceId]);
  const subscribe = useCallback(onStoreChange => {
    // Because this is stored on the window we have to make this backwards
    // compatible and check that it exists
    if (!channel || typeof channel.addStateListener !== 'function') {
      return () => {};
    }
    const unsubscribe = channel.addStateListener(onStoreChange);
    // The channel is a singleton on the top window, but when this provider is
    // hosted in an iframe (e.g. the Breeze Assistant chat), this listener
    // closure belongs to the iframe realm. If the host app tears the iframe
    // out of the DOM, React never unmounts this provider, so this subscribe's
    // own cleanup never runs and the listener is orphaned on the shared
    // channel — a later setState notify then re-renders into a torn-down realm
    // and crashes the renderer. pagehide fires on iframe teardown, so drop the
    // listener there as well. Ignore a bfcache freeze (persisted === true):
    // the document is frozen then restored on pageshow without a remount, so
    // unsubscribing then would permanently disconnect the restored provider.
    const onPageHide = event => {
      if (!event.persisted) {
        unsubscribe();
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      unsubscribe();
    };
  }, [channel]);
  const getState = useMemo(() => {
    // Because this is stored on the window we have to make this backwards
    // compatible and check that it exists
    if (channel && typeof channel.getState === 'function') {
      return channel.getState;
    }
    const defaultState = {
      appContext: {},
      isCanvasOpen: false,
      isNavCopilotAvailable: false,
      isWidgetOpen: false,
      isWidgetActive: false,
      layoutType: 'panel',
      breezeContext: {
        messageContexts: [],
        context: {},
        turnMetadata: {},
        conversationMetadata: {},
        agentId: undefined
      }
    };
    return () => defaultState;
  }, [channel]);
  const channelState = useSyncExternalStore(subscribe, getState);
  const contextValue = useMemo(() => ({
    channel,
    channelState,
    instanceId,
    telemetry
  }), [channel, channelState, instanceId, telemetry]);
  return /*#__PURE__*/_jsx(CopilotChannelContext.Provider, {
    value: contextValue,
    children: children
  });
};
export default CopilotChannelProvider;